import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { industryExperience, productDesignYears, shareOfIndustryExperience, totalDesignYears } from '../data/experience';

/** Segment tones — solid, on-brand, and distinguishable without new hues. */
const TONES = ['bg-foreground', 'bg-cobalt', 'bg-[#C9A66B]'] as const;
function formatYears(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Counts from 0 to `target` once `active` becomes true. */
function useCountUp(target: number, active: boolean, duration = 300): number {
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (reduceMotion) {
      setValue(target);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target, duration, reduceMotion]);
  return value;
}
export function ExperienceGraph() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const animatedProduct = useCountUp(productDesignYears, inView);
  const animatedTotal = useCountUp(totalDesignYears, inView, 300);
  return <div ref={ref} data-id="element-273" data-mp="12wra3x2i8sls">
      {/* Equal-scale KPIs: product design leads through weight, not size. */}
      <div className="grid grid-cols-1 gap-6 border-b border-border pb-8 sm:grid-cols-2 sm:gap-8 mb-10" data-id="element-274" data-mp="17vua333mo8mw">
        <div className="flex items-end justify-start gap-4" data-id="element-275" data-mp="2x6v32y0hmkjx">
          <span className="text-3xl font-bold tracking-tight leading-none tabular-nums md:text-4xl" style={{
          fontSize: "36px",
          fontWeight: "400"
        }} data-id="element-276" data-mp="326fxcv4pq4qb">
            {formatYears(inView ? round(animatedProduct) : 0)}
          </span>
          <span className="max-w-[10rem] text-left text-sm leading-snug text-muted" data-id="element-277" data-mp="2vtlgt79doh8p">
            years designing digital products
          </span>
        </div>

        <div className="flex items-end justify-start gap-4" data-id="element-278" data-mp="2lumk8n1w0c22">
          <span className="text-3xl font-normal tracking-tight leading-none tabular-nums text-muted md:text-4xl" style={{
          fontSize: "36px"
        }} data-id="element-279" data-mp="3jgtmvremrubs">
            {formatYears(inView ? round(animatedTotal) : 0)}
          </span>
          <span className="max-w-[10rem] text-left text-sm leading-snug text-muted" data-id="element-280" data-mp="39qi797ll3wp2">
            years across design overall
          </span>
        </div>
      </div>

      {/* Proportional bar — all career industries; KPI scope remains distinct. */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-border mb-10" aria-hidden="true" data-id="element-281" data-mp="649pin0o7j89">
        {industryExperience.map((item, i) => <motion.div key={item.industry} className={TONES[i % TONES.length]} initial={{
        width: 0
      }} animate={{
        width: inView ? `${shareOfIndustryExperience(item.years)}%` : 0
      }} transition={{
        duration: 0.28,
        delay: 0.04 + i * 0.04,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-282" data-mp="9lp4uqthlc64" data-mpi={i} />)}
      </div>

      {/* Breakdown */}
      <dl className="border-t border-border" data-id="element-283" data-mp="wk2793c5p1st">
        {industryExperience.map((item, i) => <IndustryRow key={item.industry} item={item} tone={TONES[i % TONES.length]} active={inView} data-id="element-284" data-mp="11asaob10uukv" data-mpi={i} />)}
      </dl>
    </div>;
}
function IndustryRow({
  item,
  tone,
  active
}: {
  item: (typeof industryExperience)[number];
  tone: string;
  active: boolean;
}) {
  const years = useCountUp(item.years, active);
  return <div className="flex items-baseline justify-between gap-6 py-5 border-b border-border" data-id="element-285" data-mp="2czoc70tgfcjt">
      <div className="flex items-baseline gap-3 min-w-0" data-id="element-286" data-mp="2814q90ev20rg">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 translate-y-[-1px] ${tone}`} aria-hidden="true" data-id="element-287" data-mp="280l8pkt4hedy" />
        <div className="min-w-0" data-id="element-288" data-mp="2hqwpi7xwni29">
          <dt className="text-base md:text-lg font-medium break-words" data-id="element-289" data-mp="2hrpq0r69tbby">
            {item.industry}
          </dt>
          <dd className="text-sm text-muted mt-1 break-words" data-id="element-290" data-mp="1u1lul738khw1">
            {item.companies} · {item.range}
          </dd>
        </div>
      </div>

      <dd className="flex items-baseline gap-1.5 shrink-0 tabular-nums" data-id="element-291" data-mp="231ieywileula">
        <span className="text-2xl md:text-3xl font-bold tracking-tight" style={{
        fontWeight: "400"
      }} data-id="element-292" data-mp="22zns3cnrgsek">
          {formatYears(active ? round(years) : 0)}
        </span>
        <span className="text-sm text-muted" data-id="element-293" data-mp="3efm4nqi5dwkj">
          {item.years === 1 ? 'yr' : 'yrs'}
        </span>
      </dd>
    </div>;
}